import { AlertTriangle, ChevronRight } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusBadge, type StatusVariant } from '@/shared/components/status-badge';
import { useTableSearchParams } from '@/shared/hooks/use-table-search-params';
import { attentionLabel, attentionSeverity } from '@/shared/lib/catalogue-labels';
import { cn } from '@/shared/lib/utils';

import type { AttentionItem, BrandDetails } from '../types/brand.types';

const ORDER: StatusVariant[] = ['danger', 'warning', 'info', 'neutral'];

/** Codes that are a yes/no fact about the brand or country: no count is shown. */
const FLAGS = new Set([
  'CategoryInactive',
  'NoActiveDenominations',
  'CountryInactive',
  'CountryWithoutPricing',
  'MissingArabicName',
  'MissingImage',
]);

/** The denominations-table state filter an item narrows to (for the country shown below). */
const TABLE_FILTER: Record<string, string> = {
  NotSellableInCountry: 'notSellable',
  CostSourceBlockedInCountry: 'notSellable',
  NotOfferedInCountry: 'notOffered',
};

interface Entry {
  key: string;
  label: string;
  detail?: string;
  count: number | null;
  severity: StatusVariant;
  filter?: string;
}

function toEntries(items: AttentionItem[]): Entry[] {
  const blocked = items.find((item) => item.code === 'CostSourceBlockedInCountry')?.count ?? 0;
  const notSellable = items.find((item) => item.code === 'NotSellableInCountry')?.count ?? 0;

  return (
    items
      // "Every provider is blocked" is one cause of "customers cannot buy": shown once, as its detail.
      .filter((item) => !(item.code === 'CostSourceBlockedInCountry' && notSellable > 0))
      .map((item) => ({
        key: item.code,
        label: attentionLabel(item.code),
        detail:
          item.code === 'NotSellableInCountry' && blocked > 0
            ? blocked === notSellable
              ? 'Every provider is blocked'
              : `${blocked} of them: every provider is blocked`
            : undefined,
        count: FLAGS.has(item.code) ? null : item.count,
        severity: attentionSeverity(item.code),
        filter: TABLE_FILTER[item.code],
      }))
      .sort((a, b) => ORDER.indexOf(a.severity) - ORDER.indexOf(b.severity))
  );
}

/**
 * What needs attention on a brand, as the Admin API reports it
 * (BrandCatalogueEvaluator.Attention): brand-wide issues, then each country.
 * Items for the country the table shows are one-click filters on it.
 */
export function BrandAttentionPanel({
  details,
  countryName,
  shownCountryId,
}: {
  details: BrandDetails;
  countryName: (countryId: number) => string;
  /** The country the denominations table shows states for. */
  shownCountryId: number | undefined;
}) {
  const tableState = useTableSearchParams();
  if (details.attention.length === 0) return null;

  const brandWide = details.attention.filter((item) => item.countryId === null);
  const countryIds = [
    ...new Set(
      details.attention.flatMap((item) => (item.countryId === null ? [] : [item.countryId])),
    ),
  ].sort((a, b) => Number(b === shownCountryId) - Number(a === shownCountryId));

  const groups = [
    ...(brandWide.length > 0
      ? [{ key: 'brand', title: 'Brand', countryId: null, items: brandWide }]
      : []),
    ...countryIds.map((id) => ({
      key: String(id),
      title: countryName(id),
      countryId: id,
      items: details.attention.filter((item) => item.countryId === id),
    })),
  ];

  const showInTable = (filter: string) => {
    tableState.setFilter('state', filter);
    document.getElementById('denominations-heading')?.scrollIntoView({ behavior: 'smooth' });
  };

  const issueCount = groups.reduce((sum, group) => sum + toEntries(group.items).length, 0);

  return (
    <Card className="border-status-warning-border">
      <CardHeader className="pb-3">
        <CardTitle className="flex flex-wrap items-center gap-2 text-sm">
          <AlertTriangle className="text-status-warning size-4" aria-hidden />
          Needs attention
          <span className="text-muted-foreground text-xs font-normal">
            {issueCount === 1 ? '1 issue' : `${issueCount} issues`}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div
          className={cn(
            'grid gap-4',
            groups.length > 1 && 'md:grid-cols-2',
            groups.length > 2 && 'xl:grid-cols-3',
          )}
        >
          {groups.map((group) => {
            const isShown = group.countryId !== null && group.countryId === shownCountryId;
            return (
              <section key={group.key} aria-label={`Needs attention: ${group.title}`}>
                <h3 className="text-muted-foreground mb-1.5 flex items-center gap-2 text-[11px] font-semibold tracking-wide uppercase">
                  {group.title}
                  {isShown && (
                    <span className="text-primary font-normal tracking-normal normal-case">
                      shown below
                    </span>
                  )}
                </h3>
                <ul className="divide-border border-border divide-y rounded-md border">
                  {toEntries(group.items).map((entry) => {
                    const clickable = isShown && entry.filter !== undefined;
                    const content = (
                      <>
                        <span className="min-w-0 flex-1">
                          <span className="block text-xs">{entry.label}</span>
                          {entry.detail && (
                            <span className="text-muted-foreground block text-[11px]">
                              {entry.detail}
                            </span>
                          )}
                        </span>
                        {entry.count !== null && (
                          <StatusBadge variant={entry.severity} className="tabular-nums">
                            {entry.count}
                          </StatusBadge>
                        )}
                        {entry.count === null && (
                          <StatusBadge variant={entry.severity}>
                            {entry.severity === 'danger' ? 'Blocking' : 'Check'}
                          </StatusBadge>
                        )}
                        {clickable && (
                          <ChevronRight
                            className="text-muted-foreground size-3.5 shrink-0"
                            aria-hidden
                          />
                        )}
                      </>
                    );
                    return (
                      <li key={entry.key}>
                        {clickable ? (
                          <button
                            type="button"
                            onClick={() => showInTable(entry.filter as string)}
                            className="hover:bg-muted/60 focus-visible:ring-ring flex w-full items-center gap-2 px-3 py-2 text-start focus-visible:ring-2 focus-visible:outline-none"
                            title="Show these denominations"
                          >
                            {content}
                          </button>
                        ) : (
                          <div className="flex items-center gap-2 px-3 py-2">{content}</div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
